import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SeatIndicator } from "@/components/SeatIndicator";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMsg } from "@/lib/api";
import { toast } from "sonner";
import { AudienceIcon } from "@/components/AudienceIcon";
import { Calendar, Clock, Tag, Users, Bell, Ticket } from "lucide-react";
import { priceLabel } from "@/lib/price";

const AUDIENCE = { adult: "Felnőtt", children: "Gyermek", together: "Közös (szülő-gyerek)" };
const AUDIENCE_HEX = { adult: "#E23D50", children: "#F59314", together: "#B58CE0" };

export const ProgramDialog = ({ program, open, onOpenChange, onChanged }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!program) return null;

  const full = program.available_seats <= 0;

  const book = async () => {
    if (!user) { navigate("/belepes"); return; }
    setBusy(true);
    try {
      await api.post("/bookings", { program_id: program.program_id });
      toast.success("Sikeres foglalás! Jóváhagyás után e-mailt küldünk.");
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const notify = async () => {
    if (!user) { navigate("/belepes"); return; }
    setBusy(true);
    try {
      await api.post(`/programs/${program.program_id}/notify`);
      toast.success("Értesítünk, ha felszabadul egy hely!");
    } catch (e) {
      toast.error(apiErrorMsg(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white p-0 h-[85vh] max-h-[680px] overflow-hidden flex flex-col border-black/5" data-testid="program-dialog">
        <div className="flex-1 overflow-y-auto min-h-0" data-testid="program-dialog-scroll">
          {program.photo_url && (
            <img src={program.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${program.photo_url}` : program.photo_url}
                 alt={program.title} className="w-full h-40 object-cover" />
          )}
          <div className="px-7 pt-6 pb-4">
            <DialogHeader>
              <span className="flex items-center gap-2" style={{ color: AUDIENCE_HEX[program.audience] || AUDIENCE_HEX.adult }}><AudienceIcon audience={program.audience} size={22} strokeWidth={1.7} /><span className="overline">{AUDIENCE[program.audience] || program.audience}</span></span>
              <DialogTitle className="font-serif text-3xl font-normal text-ink mt-1">{program.title}</DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-sm leading-relaxed text-inkmuted whitespace-pre-line">{program.description}</p>

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-ink">
              <div className="flex items-center gap-2"><Calendar size={15} strokeWidth={1.5} className="text-poppy" />{program.start_date}</div>
              <div className="flex items-center gap-2 text-base font-bold"><Clock size={16} strokeWidth={2} className="text-poppy" />{program.start_time}–{program.end_time}</div>
              {!program.pass_only && <div className="flex items-center gap-2"><Tag size={15} strokeWidth={1.5} className="text-poppy" />{priceLabel(program.price, program.audience)}</div>}
              <div className="flex items-center gap-2"><AudienceIcon audience={program.audience} size={16} className="text-poppy" />{AUDIENCE[program.audience]}</div>
            </div>

            {(program.pass_only || program.pass_eligible) && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal text-xs font-medium px-3 py-1.5" data-testid="pass-eligible-badge">
                <Ticket size={14} strokeWidth={1.8} /> {program.pass_only ? "csak bérlettel" : "bérlettel is"}
              </div>
            )}

            <div className="mt-6"><SeatIndicator info={program} showCount={false} /></div>
          </div>
        </div>

        <div className="px-7 py-4 shrink-0 border-t border-stone-100 bg-white">
          {full ? (
            <button onClick={notify} disabled={busy} data-testid="notify-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full border border-ink/15 text-ink py-3 text-sm hover:bg-stone-50 transition-colors">
              <Bell size={16} strokeWidth={1.5} /> Várólistára jelentkezem
            </button>
          ) : (
            <button onClick={book} disabled={busy} data-testid="dialog-book-btn"
              className="w-full rounded-full bg-poppy text-white py-3 text-sm hover:bg-poppy/90 transition-colors disabled:opacity-60">
              {busy ? "Foglalás..." : "Helyfoglalás"}
            </button>
          )}
          <p className="mt-3 text-xs text-inkmuted text-center">A fizetés első alkalommal a helyszínen.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
